// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { 
  ArenaMode, 
  CurrencyType, 
  Currency, 
  ArenaReward, 
  Arena, 
  LudoConfiguration
} from '../types/ludoSettings';

export class CurrencyClass implements Currency {
  type: CurrencyType;
  amount: number;

  constructor(type: CurrencyType = CurrencyType.LudoCoin, amount: number = 0) {
    this.type = type;
    this.amount = amount;
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): CurrencyClass {
    return new CurrencyClass(obj.type || CurrencyType.LudoCoin, obj.amount || 0);
  }

  /**
   * Convert to plain object
   */
  toObject(): Currency {
    return {
      type: this.type,
      amount: this.amount
    };
  }

  /**
   * Create a copy of this currency
   */
  clone(): CurrencyClass {
    return new CurrencyClass(this.type, this.amount);
  }

  /**
   * Check if this currency is valid
   */
  isValid(): boolean {
    return this.amount >= 0 && Object.values(CurrencyType).includes(this.type);
  }
}

export class ArenaRewardClass implements ArenaReward {
  type: CurrencyType;
  amount: number;

  constructor(type: CurrencyType = CurrencyType.LudoCoin, amount: number = 0) {
    this.type = type;
    this.amount = amount;
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): ArenaRewardClass {
    return new ArenaRewardClass(obj.type || CurrencyType.LudoCoin, obj.amount || 0);
  }

  /**
   * Convert to plain object
   */
  toObject(): ArenaReward {
    return {
      type: this.type,
      amount: this.amount
    };
  }

  /**
   * Create a copy of this reward
   */
  clone(): ArenaRewardClass {
    return new ArenaRewardClass(this.type, this.amount);
  }

  /**
   * Check if this reward is valid
   */
  isValid(): boolean {
    return this.amount >= 0 && Object.values(CurrencyType).includes(this.type);
  }
}

export class ArenaClass implements Arena {
  name: string;
  mode: ArenaMode;
  fee_cost: CurrencyClass;
  rewards: Record<string, ArenaRewardClass[]>;
  consolation_reward: ArenaRewardClass[];
  json_config: Record<string, any>;

  constructor(
    name: string = '',
    mode: ArenaMode = ArenaMode.Mode2POnline,
    fee_cost?: CurrencyClass,
    rewards?: Record<string, ArenaRewardClass[]>,
    consolation_reward?: ArenaRewardClass[],
    json_config?: Record<string, any>
  ) {
    this.name = name;
    this.mode = mode;
    this.fee_cost = fee_cost || new CurrencyClass(CurrencyType.LudoCoin, 100);
    this.rewards = rewards || this.getDefaultRewards(mode);
    this.consolation_reward = consolation_reward || [];
    this.json_config = json_config || {};
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): ArenaClass {
    const arena = new ArenaClass();
    arena.name = obj.name || '';
    arena.mode = obj.mode || ArenaMode.Mode2POnline;
    arena.fee_cost = obj.fee_cost ? CurrencyClass.fromObject(obj.fee_cost) : new CurrencyClass();
    
    // Convert rewards
    arena.rewards = {};
    if (obj.rewards) {
      Object.entries(obj.rewards).forEach(([rank, rewardArray]: [string, any]) => {
        if (Array.isArray(rewardArray)) {
          arena.rewards[rank] = rewardArray.map((reward: any) => ArenaRewardClass.fromObject(reward));
        }
      });
    }
    
    // Convert consolation rewards
    arena.consolation_reward = [];
    if (obj.consolation_reward && Array.isArray(obj.consolation_reward)) {
      arena.consolation_reward = obj.consolation_reward.map((reward: any) => ArenaRewardClass.fromObject(reward));
    }
    
    arena.json_config = obj.json_config || {};
    return arena;
  }

  /**
   * Convert to plain object
   */
  toObject(): Arena {
    const rewards: Record<string, ArenaReward[]> = {};
    Object.entries(this.rewards).forEach(([rank, rewardArray]) => {
      rewards[rank] = rewardArray.map(reward => reward.toObject());
    });
    
    return {
      name: this.name,
      mode: this.mode,
      fee_cost: this.fee_cost.toObject(),
      rewards,
      consolation_reward: this.consolation_reward.map(reward => reward.toObject()),
      json_config: this.json_config
    };
  }

  /**
   * Get default rewards based on arena mode
   */
  private getDefaultRewards(mode: ArenaMode): Record<string, ArenaRewardClass[]> {
    const isMultiplayer = mode === ArenaMode.Mode4POnline || mode === ArenaMode.Mode4PWithFriends;
    
    if (isMultiplayer) {
      return {
        "1": [new ArenaRewardClass(CurrencyType.LudoCoin, 300)],
        "2": [new ArenaRewardClass(CurrencyType.LudoCoin, 60)],
        "3": [new ArenaRewardClass(CurrencyType.LudoCoin, 40)]
      };
    } else {
      return {
        "1": [new ArenaRewardClass(CurrencyType.LudoCoin, 160)],
        "2": [new ArenaRewardClass(CurrencyType.LudoCoin, 40)]
      };
    }
  }

  /**
   * Create a copy of this arena
   */
  clone(): ArenaClass {
    const clonedRewards: Record<string, ArenaRewardClass[]> = {};
    Object.entries(this.rewards).forEach(([rank, rewardArray]) => {
      clonedRewards[rank] = rewardArray.map(reward => reward.clone());
    });

    return new ArenaClass(
      this.name,
      this.mode,
      this.fee_cost.clone(),
      clonedRewards,
      this.consolation_reward.map(reward => reward.clone()),
      { ...this.json_config }
    );
  }

  /**
   * Validate arena configuration
   */
  validate(): string[] {
    const errors: string[] = [];

    if (!this.name || this.name.trim() === '') {
      errors.push('Arena name is required');
    }

    if (!Object.values(ArenaMode).includes(this.mode)) {
      errors.push('Invalid arena mode');
    }

    if (!this.fee_cost.isValid()) {
      errors.push('Invalid entry fee configuration');
    }

    // Validate rewards
    Object.entries(this.rewards).forEach(([rank, rewardArray]) => {
      if (!rewardArray || rewardArray.length === 0) {
        errors.push(`No rewards configured for rank ${rank}`);
      } else {
        rewardArray.forEach((reward, index) => {
          if (!reward.isValid()) {
            errors.push(`Invalid reward ${index + 1} for rank ${rank}`);
          }
        });
      }
    });

    // Validate consolation rewards
    this.consolation_reward.forEach((reward, index) => {
      if (!reward.isValid()) {
        errors.push(`Invalid consolation reward ${index + 1}`);
      }
    });

    return errors;
  }

  /**
   * Check if arena is valid
   */
  isValid(): boolean {
    return this.validate().length === 0;
  }

  /**
   * Update a field in the arena
   */
  updateField(field: string, value: any): void {
    if (field.includes('.')) {
      // Handle nested fields like 'fee_cost.amount' or 'rewards.1.0.type'
      const parts = field.split('.');
      let current: any = this;
      
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    } else {
      (this as any)[field] = value;
    }
  }
}

export class LudoSettingsClass implements LudoConfiguration {
  arenas: Record<string, ArenaClass>;

  constructor(arenas?: Record<string, ArenaClass>) {
    this.arenas = arenas || this.getDefaultArenas();
  }

  /**
   * Get default arena configuration
   */
  private getDefaultArenas(): Record<string, ArenaClass> {
    return {
      "2P_ONLINE_ARENA": new ArenaClass(
        "2P_ONLINE_ARENA",
        ArenaMode.Mode2POnline,
        new CurrencyClass(CurrencyType.LudoCoin, 100),
        {
          "1": [new ArenaRewardClass(CurrencyType.LudoCoin, 160)],
          "2": [new ArenaRewardClass(CurrencyType.LudoCoin, 40)]
        }
      ),
      "2P_WITH_FRIENDS_ARENA": new ArenaClass(
        "2P_WITH_FRIENDS_ARENA",
        ArenaMode.Mode2PWithFriends,
        new CurrencyClass(CurrencyType.LudoCoin, 100),
        {
          "1": [new ArenaRewardClass(CurrencyType.LudoCoin, 160)],
          "2": [new ArenaRewardClass(CurrencyType.LudoCoin, 40)]
        }
      ),
      "4P_ONLINE_ARENA": new ArenaClass(
        "4P_ONLINE_ARENA",
        ArenaMode.Mode4POnline,
        new CurrencyClass(CurrencyType.LudoCoin, 100),
        {
          "1": [new ArenaRewardClass(CurrencyType.LudoCoin, 300)],
          "2": [new ArenaRewardClass(CurrencyType.LudoCoin, 60)],
          "3": [new ArenaRewardClass(CurrencyType.LudoCoin, 40)]
        }
      ),
      "4P_WITH_FRIENDS_ARENA": new ArenaClass(
        "4P_WITH_FRIENDS_ARENA",
        ArenaMode.Mode4PWithFriends,
        new CurrencyClass(CurrencyType.LudoCoin, 100),
        {
          "1": [new ArenaRewardClass(CurrencyType.LudoCoin, 300)],
          "2": [new ArenaRewardClass(CurrencyType.LudoCoin, 60)],
          "3": [new ArenaRewardClass(CurrencyType.LudoCoin, 40)]
        }
      )
    };
  }

  /**
   * Serialize to JSON string with validation
   */
  toJSON(): string {
    try {
      // Validate before serialization
      const validationErrors = this.validate();
      if (validationErrors.length > 0) {
        console.warn('Validation warnings during serialization:', validationErrors);
      }
      
      // Use manual serialization (more reliable)
      return JSON.stringify(this.toObject(), null, 2);
    } catch (error) {
      console.error('Serialization failed:', error);
      // Fallback to basic JSON.stringify
      return JSON.stringify({
        arenas: this.arenas
      }, null, 2);
    }
  }

  /**
   * Deserialize from JSON string with validation
   */
  static fromJSON(jsonString: string): LudoSettingsClass {
    try {
      const plainObject = JSON.parse(jsonString);
      return LudoSettingsClass.fromObject(plainObject);
    } catch (error) {
      console.error('JSON deserialization failed:', error);
      return new LudoSettingsClass(); // Return default settings on error
    }
  }

  /**
   * Create from plain object with validation
   */
  static fromObject(obj: any): LudoSettingsClass {
    try {
      const instance = new LudoSettingsClass();
      
      if (obj.arenas) {
        instance.arenas = {};
        Object.entries(obj.arenas).forEach(([key, arenaObj]: [string, any]) => {
          instance.arenas[key] = ArenaClass.fromObject(arenaObj);
        });
      }
      
      // Validate created instance
      const validationErrors = instance.validate();
      if (validationErrors.length > 0) {
        console.warn('Validation warnings after object conversion:', validationErrors);
      }
      
      return instance;
    } catch (error) {
      console.error('Object conversion failed:', error);
      return new LudoSettingsClass(); // Return default settings on error
    }
  }

  /**
   * Convert to plain object with validation
   */
  toObject(): any {
    try {
      // Validate before conversion
      const validationErrors = this.validate();
      if (validationErrors.length > 0) {
        console.warn('Validation warnings during object conversion:', validationErrors);
      }
      
      // Manual conversion (more reliable)
      const arenas: Record<string, any> = {};
      Object.entries(this.arenas).forEach(([key, arena]) => {
        arenas[key] = arena.toObject();
      });
      
      return {
        arenas
      };
    } catch (error) {
      console.error('Object conversion failed:', error);
      // Fallback to basic conversion
      return {
        arenas: this.arenas
      };
    }
  }

  /**
   * Create a deep copy of the settings
   */
  clone(): LudoSettingsClass {
    const clonedArenas: Record<string, ArenaClass> = {};
    Object.entries(this.arenas).forEach(([key, arena]) => {
      clonedArenas[key] = arena.clone();
    });
    return new LudoSettingsClass(clonedArenas);
  }

  /**
   * Validate all arena configurations
   */
  validate(): string[] {
    const errors: string[] = [];

    if (!this.arenas || Object.keys(this.arenas).length === 0) {
      errors.push('No arenas configured');
      return errors;
    }

    Object.entries(this.arenas).forEach(([arenaKey, arena]) => {
      const arenaErrors = arena.validate();
      arenaErrors.forEach(error => {
        errors.push(`${arenaKey}: ${error}`);
      });
    });

    return errors;
  }

  /**
   * Check if all settings are valid
   */
  isValid(): boolean {
    return this.validate().length === 0;
  }

  /**
   * Reset to default configuration
   */
  resetToDefaults(): void {
    this.arenas = this.getDefaultArenas();
  }

  /**
   * Update an arena field
   */
  updateArenaField(arenaKey: string, field: string, value: any): void {
    if (this.arenas[arenaKey]) {
      this.arenas[arenaKey].updateField(field, value);
    }
  }

  /**
   * Add a new arena
   */
  addArena(key: string, arena: ArenaClass): void {
    this.arenas[key] = arena;
  }

  /**
   * Remove an arena
   */
  removeArena(key: string): void {
    delete this.arenas[key];
  }

  /**
   * Get arena by key
   */
  getArena(key: string): ArenaClass | undefined {
    return this.arenas[key];
  }

  /**
   * Get all arena keys
   */
  getArenaKeys(): string[] {
    return Object.keys(this.arenas);
  }

  /**
   * Check if settings have changed compared to another instance
   */
  hasChangedFrom(other: LudoSettingsClass): boolean {
    return this !== other;
  }

  /**
   * Update from another LudoSettingsClass instance
   */
  updateFrom(other: LudoSettingsClass): void {
    this.arenas = other.clone().arenas;
  }

  /**
   * Get statistics about the configuration
   */
  getStats(): {
    totalArenas: number;
    onlineArenas: number;
    friendsArenas: number;
    twoPlayerArenas: number;
    fourPlayerArenas: number;
  } {
    const arenas = Object.values(this.arenas);
    return {
      totalArenas: arenas.length,
      onlineArenas: arenas.filter(a => a.mode.includes('ONLINE')).length,
      friendsArenas: arenas.filter(a => a.mode.includes('FRIENDS')).length,
      twoPlayerArenas: arenas.filter(a => a.mode.includes('2P')).length,
      fourPlayerArenas: arenas.filter(a => a.mode.includes('4P')).length
    };
  }
}
